import clsx from 'clsx';
import * as React from 'react';

import type { InlineLinkData } from '@gitbook/doc-core';
import type { InlineProps } from '@gitbook/slate-react';
import { type IconDefinition, renderIcon } from '@gitbook/spine-react';

import {
    type UIResolvedContentReference,
    useContentReference,
    useContentRefNavigationProps,
} from '../../ContentReferences';
import {
    InlineContentRefEditPopover,
    InlineContentRefPreviewPopover,
    useInlineTagProps,
    useIsInlineActive,
} from '../inlines';
import { ALLOWED_CONTENT_REFS_IN_INLINE_LINK } from './util';

/**
 * Component to render an inline link.
 */
export function InlineLink(props: InlineProps) {
    const { attributes, children } = props;
    return (
        <React.Suspense
            fallback={
                <Anchor {...attributes} aria-busy>
                    {children}
                </Anchor>
            }
        >
            <ResolvedInlineLink {...props} />
        </React.Suspense>
    );
}

/**
 * Resolves an inline link, it suspenses.
 */
function ResolvedInlineLink(props: InlineProps) {
    const { node, attributes, children, editor } = props;
    const data = node.data.toFrozenObject<InlineLinkData>();
    const resolved = useContentReference(data.ref, { resolveAnchorText: false });
    const [isActive, inlineProps] = useIsInlineActive(props);
    const anchor = (
        <ContentRefAnchor
            resolved={resolved}
            readOnly={editor.readOnly}
            {...attributes}
            {...inlineProps}
        >
            {children}
        </ContentRefAnchor>
    );

    if (isActive) {
        if (editor.readOnly) {
            return (
                <InlineContentRefPreviewPopover
                    label="Link"
                    resolved={resolved}
                    contentRef={data.ref}
                >
                    {anchor}
                </InlineContentRefPreviewPopover>
            );
        }
        return (
            <InlineContentRefEditPopover
                label="Link"
                resolved={resolved}
                contentRef={data.ref}
                accept={ALLOWED_CONTENT_REFS_IN_INLINE_LINK}
                {...props}
            >
                {anchor}
            </InlineContentRefEditPopover>
        );
    }

    return anchor;
}

type ContentRefAnchorProps = Omit<AnchorProps, 'icon'> & {
    resolved: UIResolvedContentReference;
    readOnly: boolean;
};

/**
 * Anchor that renders a resolved content ref.
 */
const ContentRefAnchor = React.forwardRef(function ContentRefAnchor(
    props: ContentRefAnchorProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
) {
    const { resolved, readOnly, ...rest } = props;
    const navProps = useContentRefNavigationProps(readOnly ? props.resolved : null, props);
    return (
        <Anchor
            ref={ref}
            icon={
                !resolved.resolution && resolved.secondaryIcon ? resolved.secondaryIcon : undefined
            }
            className={clsx(readOnly ? undefined : 'cursor-[inherit]', rest.className)}
            {...rest}
            {...navProps}
        />
    );
});

type AnchorProps = React.ComponentPropsWithoutRef<'a'> & { icon?: IconDefinition | undefined };

/**
 * The low level anchor used to render the inline link.
 */
const Anchor = React.forwardRef(function Anchor(
    props: AnchorProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
) {
    const { icon, ...rest } = props;
    const tagProps = useInlineTagProps(rest);
    return (
        <a
            ref={ref}
            data-link=""
            {...tagProps}
            className={clsx(
                'text-primary underline underline-offset-2 hover:no-underline',
                tagProps.className
            )}
        >
            {icon ? renderIcon(icon, { className: 'mr-1 text-[0.8em]' }) : null}
            {tagProps.children}
        </a>
    );
});
